import React, { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { DATE_FNS_LONG_DATE_FORMAT } from "../../shared/constants";

import { DEFAULT_USER, buildColumns, buildTeamColumns, createUserStatusOptions } from "./lib/constants";
import MultiSelectList from "../../components/form-inputs/custom/react-multi-select";
import { Select } from "components/form-inputs/inputs/select";
import { useUserTeams } from "../../shared/hooks/api/use-user-teams";
import { useUsersRoles } from "../../shared/hooks/api/use-users-roles";
import { useUsers } from "../../shared/hooks/api/use-users";
import { useUserDetails } from "../../shared/hooks/api/use-user-details";
import ReactTable from "react-table";
import * as notify from "../../shared/notify";
import { useParams } from "react-router-dom";

const UserDetailIndex = () => {
    const params = useParams();
    const userId = params.id;
    const userStatusOptions = useMemo(createUserStatusOptions, []);
    const [clearSelections, setClearSelections] = useState(false);
    const [userStatus, setUserStatus] = useState();
    const [user, setUser] = useState({ ...DEFAULT_USER });
    const [roles, setRoles] = useState([]);
    const [teams, setTeams] = useState([]);
    const { isSuccess: isUserTeamsFetched, data: userTeams } = useUserTeams();
    const { isSuccess: isUserRolesFetched, data: userRoles } = useUsersRoles();
    const { data: userDetails, refetch: refetchUserDetails, isSuccess: isUserDetailsFetched } = useUserDetails(userId);
    const { mutate: saveUser } = useUsers();
    const [userSettings, setUserSettings] = useState({
        userRoles: [],
        userAssignmentGroups: [],
        isActive: true,
        isAnyRoleRemoved: false,
    });

    useEffect(() => {
        if (user && user.roles && user.teams) {
            const [{ value: active }, { value: inactive }] = userStatusOptions;

            setUserSettings({
                isAnyRoleRemoved: false,
                userRoles: user.roles,
                userAssignmentGroups: user.teams,
                isActive: user.isActive,
            });

            const isActive = user.isActive ? active : inactive;
            setUserStatus(isActive.toString());
        }
    }, [user]);

    useEffect(() => {
        if (clearSelections) {
            setClearSelections(false);
        }
    }, [clearSelections]);

    useEffect(() => {
        if (userDetails && isUserDetailsFetched) {
            setUser(userDetails);
        }
    }, [userDetails, isUserDetailsFetched]);

    const handleUserTeams = (name, values) => {
        if (isUserTeamsFetched) {
            setTeams(values);
        }
    };

    const handleUserRoles = (name, values) => {
        if (isUserRolesFetched) {
            setRoles(values);
        }
    };

    const handleSave = async () => {
        const mapValues = ({ id, key, value }) => id || value || key;

        const filterDuplicates = (value, pos, all) => {
            return all.indexOf(value) === pos;
        };

        const { userRoles: existingUserRoles, userAssignmentGroups } = userSettings;
        const assignedRoleIds = [...roles.map(mapValues), ...existingUserRoles.map(mapValues)];
        const assignedGroups = [...teams.map(mapValues), ...userAssignmentGroups.map(mapValues)];

        // user status set to "disabled"
        const [{ value: active }, { value: disabled }] = userStatusOptions;

        if (userStatus === disabled) {
            const confirm = await notify.confirm(
                "Any tasks assigned to this user will be unassigned. Are you sure you want to deactivate this user’s account?"
            );

            if (!confirm) {
                setUserStatus(active);

                return;
            }
        }

        if (userSettings.isAnyRoleRemoved) {
            const confirm = await notify.confirm(
                "Any tasks assigned to this user as part of the team will be unassigned from the user. Are you sure you want to remove them from this team?"
            );

            if (!confirm) {
                return;
            }
        }

        saveUser(
            {
                userId,
                roleIds: assignedRoleIds.filter(filterDuplicates),
                groupAssignmentKeys: assignedGroups.filter(filterDuplicates),
                userStatus,
            },
            {
                onSuccess: async (data) => {
                    if (data && data.success === false) {
                        return notify.error("You do not have the permissions to perform this action.");
                    }

                    notify.success("User settings saved");
                    setClearSelections(true);
                    setUserSettings({ ...userSettings, isAnyRoleRemoved: false });
                    refetchUserDetails();
                },
            }
        );
    };

    const handleDeleteTeam = async (teamId) => {
        setUserSettings({
            ...userSettings,
            userAssignmentGroups: userSettings.userAssignmentGroups.filter(({ key }) => key !== teamId),
            isAnyRoleRemoved: true,
        });
    };

    const handleDeleteRole = async (roleId) => {
        setUserSettings({
            ...userSettings,
            userRoles: userSettings.userRoles.filter(({ id }) => id !== roleId),
        });
    };

    const createdAt = user.createdAt ? format(new Date(user.createdAt), DATE_FNS_LONG_DATE_FORMAT) : "";
    const lastLogin = user.lastLogin ? format(new Date(user.lastLogin), DATE_FNS_LONG_DATE_FORMAT) : "";

    return (
        <>
            <div className="button-group">
                <h1 className="txt-nowrap">{user.fullName}</h1>
                <div className="button-group -right">
                    <button className="button -primary" onClick={handleSave}>
                        SAVE
                    </button>
                </div>
            </div>
            <p>
                <b>Email: </b>
                {user.email}
                <br />
                <b>Created: </b>
                {createdAt}
                <br />
                <b>Last Login: </b>
                {lastLogin}
                <br />
            </p>
            <h3>Status</h3>
            <div className="form-field flex">
                <Select
                    className="flex-33"
                    label="User status"
                    name="userStatus"
                    options={userStatusOptions}
                    value={userStatus}
                    onChange={(e) => setUserStatus(e.target.value)}
                />
            </div>
            <h3>Teams</h3>
            <MultiSelectList
                name="Add Team"
                options={userTeams}
                updateOnChange={handleUserTeams}
                showPagination={false}
                clearSelections={clearSelections}
            />
            <ReactTable data={userSettings.userAssignmentGroups} columns={buildTeamColumns(handleDeleteTeam)} minRows="5" showPagination={false} />
            <br />
            <h3>Roles</h3>
            <MultiSelectList name="Add Role" options={userRoles} updateOnChange={handleUserRoles} clearSelections={clearSelections} />
            <h3>User Roles</h3>
            <ReactTable data={userSettings.userRoles} columns={buildColumns(handleDeleteRole)} minRows={5} showPagination={false} />
        </>
    );
};

export default UserDetailIndex;
